<template lang="pug">
.app(ref="root" :data-is-epilogue="isEpiloguePage")
  .app__bg1(ref="bg1")
  .app__bg2(ref="bg2")
  .app__bg3(ref="bg3")
  .app__content
    router-view(@init-video="playVideo")
  transition
    .app__movie(v-show="isMovieActive")
      video(:src="videoSrc" ref="video" :style="videoStyle" playsinline)
</template>

<script>
import _ from "lodash";
import { mapState, mapActions, mapGetters } from "vuex";
import { BASE_PATH } from "@/const/config";
import { audioApi } from "@/js/soundManager";
import { TweenMax, TimelineMax, SteppedEase } from "gsap";

const VIDEO_ASPECT = {
  PC: 1200 / 630,
  SP: 1080 / 1920
};

export default {
  name: "app",
  data: () => ({
    masterTimeline: null,
    videoInitialized: false,
    windowWidth: 0,
    windowHeight: 0
  }),
  computed: {
    ...mapState(["soundFlag", "isAnimBg", "isMovieActive", "bgNumber"]),
    ...mapGetters(["resultData"]),
    isSP() {
      return /iphone|ipad|ipod|android/i.test(navigator.userAgent);
    },
    videoSrc() {
      if (!this.resultData) return null;
      const movieId = this.resultData.movieId;
      const device = this.isSP ? "sp" : "pc";
      return `${BASE_PATH}video/yonimo_${movieId}_${device}.mp4`;
    },
    videoStyle() {
      const windowAspect = this.windowWidth / this.windowHeight;
      const videoAspect = this.isSP ? VIDEO_ASPECT.SP : VIDEO_ASPECT.PC;
      const videoSize =
        windowAspect < videoAspect
          ? [this.windowHeight * videoAspect, this.windowHeight]
          : [this.windowWidth, this.windowWidth / videoAspect];
      return {
        width: `${videoSize[0]}px`,
        height: `${videoSize[1]}px`,
        marginLeft: `${-videoSize[0] / 2}px`,
        marginTop: `${-videoSize[1] / 2}px`
      };
    },
    isEpiloguePage() {
      return this.$route.name === "epilogue";
    }
  },
  methods: {
    ...mapActions(["turnOnMovie", "turnOffMovie"]),
    playVideo() {
      const el = this.$refs.video;
      this.turnOnMovie();
      el.play();
    },
    videoFirstTap() {
      if (this.videoInitialized) return;
      this.videoInitialized = true;
      const el = this.$refs.video;
      if (el.paused) {
        const onPlaying = () => {
          el.pause();
          el.removeEventListener("playing", onPlaying);
        };
        el.addEventListener("playing", onPlaying);
        el.play();
      }
    },
    initFirstTapHandler() {
      this.$refs.root.addEventListener("click", () => {
        audioApi.ping();
        if (this.videoSrc) {
          this.videoFirstTap();
        }
      });
    },
    appearNoise() {
      const bg1 = this.$refs.bg1;
      const bg2 = this.$refs.bg2;
      const bg3 = this.$refs.bg3;

      const timeline = new TimelineMax();

      timeline
        .set(bg1, {
          visibility: "visible",
          delay: 0.5
        })
        .set(bg1, {
          visibility: "hidden",
          delay: 0.1
        })
        .set(bg1, {
          visibility: "visible",
          delay: 0.4
        })
        .set(bg1, {
          visibility: "hidden",
          delay: 0.1
        })
        .set(bg2, {
          visibility: "visible",
          delay: 0.2
        })
        .set(bg2, {
          visibility: "hidden",
          delay: 0.2
        })
        .set(bg2, {
          visibility: "visible",
          delay: 0.3
        })
        .set(bg2, {
          visibility: "hidden",
          delay: 0.2
        })
        .set(bg1, {
          visibility: "visible",
          delay: 0.1
        })
        .set(bg1, {
          visibility: "hidden",
          delay: 0.1
        })
        .set(bg2, {
          visibility: "visible"
        })
        .set(bg2, {
          visibility: "hidden",
          delay: 0.2
        })
        .set(bg3, {
          visibility: "visible"
        });
      return timeline;
    },
    repeatBg() {
      const bg3 = this.$refs.bg3;
      let x = _.random(0, 500);
      let y = _.random(0, 50);

      TweenMax.to(bg3, 1, {
        backgroundPositionX: `${x}px`,
        backgroundPositionY: `${y}px`,
        repeat: 1,
        ease: SteppedEase.config(10)
      });
    },
    hideBg() {
      const bg3 = this.$refs.bg3;
      TweenMax.set(bg3, {
        visibility: "hidden"
      });
    },
    initTimeline() {
      this.masterTimeline = new TimelineMax();
      this.masterTimeline.add(this.appearNoise(), "noise");
      this.masterTimeline.stop();
    },
    initWindowSizeHandler() {
      const handleWindowResize = () => {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
      };
      window.addEventListener("resize", handleWindowResize);
      handleWindowResize();
    }
  },
  mounted() {
    this.initFirstTapHandler();
    this.$refs.video.addEventListener("ended", () => {
      this.turnOffMovie();
      setTimeout(() => {
        this.$router.push({ name: "epilogue" });
      });
    });
    this.initTimeline();
    this.initWindowSizeHandler();
  },
  watch: {
    isAnimBg: function(newValue) {
      if (newValue) {
        this.masterTimeline.play();
        this.masterTimeline.eventCallback("onComplete", () => {
          this.repeatBg();
        });
      } else {
        this.hideBg();
      }
    },
    videoSrc: function() {
      this.videoInitialized = false;
    },
    bgNumber: function(num) {
      if (num === 1) {
        TweenMax.set(this.$refs.bg1, {
          visibility: "visible"
        });
      } else if (num === 2) {
        TweenMax.set(this.$refs.bg2, {
          visibility: "visible"
        });
      } else {
        TweenMax.set(this.$refs.bg1, {
          visibility: "hidden"
        });
        TweenMax.set(this.$refs.bg2, {
          visibility: "hidden"
        });
      }
    }
  }
};
</script>

<style lang="scss">
.app {
  position: relative;
  min-height: 100vh;
  background-image: url("./assets/images/bg_normal.jpg");
  background-size: 100% auto;
  background-attachment: fixed;
  &[data-is-epilogue="true"] {
    background-image: none;
    background-color: #000;
  }
}

.app__bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  visibility: hidden;
}

.app__bg1 {
  @extend .app__bg;
  background-image: url("./assets/images/bg_glitch_1.png");
  background-position: center bottom;
  background-size: 100% 100%;
}

.app__bg2 {
  @extend .app__bg;
  background-image: url("./assets/images/bg_glitch_2.png");
  background-position: center bottom;
  background-size: 100% 100%;
}

.app__bg3 {
  @extend .app__bg;
  background-image: url("./assets/images/bg_glitch_3.png");
  background-position: center bottom;
  background-size: 100% auto;
}

.app__content {
  margin: auto;
  max-width: 375px;
  background-color: #fff;
}

.app__movie {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.2s;

  video {
    position: absolute;
    display: block;
    left: 50%;
    top: 50%;
  }

  &.v-enter,
  &.v-leave-to {
    opacity: 0;
  }
}
</style>
